import React, { useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import CareplanCreatorForm from "./CareplanCreatorForm";
import UserContext from "contexts/UserContext";

import { GET_CAREPLAN_TEMPLATE } from "graphql/TemplateQueries";
import {
  LOAD_LIST_DATA,
  GET_DIAGNOSIS_CATEGORIES,
  GET_TENANT_CONFIG,
} from "graphql/ListDataQueries";

import { GET_SITES } from "graphql/SiteQueries";
import { TemplateInterface } from "interfaces/templates";
import { TenantConfig } from "interfaces/common";
import {
  useTenantConfig,
  initialTenantConfig,
} from "contexts/TenantConfigContext";
import { getTenantConfig } from "shared/utils";

const roListCategories = [
  "patientPosition",
  "extentOfCT",
  "immobilisation",
  "bladder",
  "stomach",
  "is3dBolus",
  "bolusLocationType",
  "bolusThickness",
  "bolusFrequency",
  "technique",
  "phase",
  "isocenter",
  "modality",
  "imaging",
  "imagingTechnique",
  "frequency",
  "doseto",
  "treatingDepartment",
  "qcl",
  "credential",
  "breathHold",
  "compression",
  "mouth",
];

const moListCategories = [
  "route",
  "form",
  "drugType",
  "units",
  "solution",
  "durationunits",
];

interface TemplateData {
  template: TemplateInterface;
}
interface TenantConfigData {
  tenantConfig: TenantConfig[];
}
const CareplanCreatorFormApollo = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const user = useContext(UserContext);
  const { setTenantConfig } = useTenantConfig();
  const {
    loading,
    error,
    data,
    refetch: reloadTemplate,
  } = useQuery<TemplateData>(GET_CAREPLAN_TEMPLATE, {
    variables: { templateId },
  });

  // Memoize regions array to track changes and avoid unnecessary refetches
  const regions = useMemo(
    () => data?.template?.regions?.map((region) => region.name) ?? [],
    [data?.template?.regions],
  );

  // Create a stable string representation of regions for dependency tracking
  const regionsKey = useMemo(
    () => JSON.stringify([...regions].sort()),
    [regions],
  );

  const { data: tenantConfigData, refetch: refetchTenantConfig } =
    useQuery<TenantConfigData>(GET_TENANT_CONFIG, {
      variables: {
        regions,
      },
      fetchPolicy: "cache-and-network",
      skip: !data?.template?.regions || regions.length === 0,
      notifyOnNetworkStatusChange: true,
    });

  // Refetch tenant config when regions change (Apollo should auto-refetch on variables change,
  // but we add this as a safeguard to ensure updates happen)
  useEffect(() => {
    if (regions.length > 0 && data?.template?.regions) {
      refetchTenantConfig({
        variables: { regions },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionsKey]);

  useEffect(() => {
    if (setTenantConfig && tenantConfigData) {
      setTenantConfig(getTenantConfig(tenantConfigData) ?? initialTenantConfig);
    }
  }, [tenantConfigData, setTenantConfig]);

  const listCategories = ([] as string[]).concat(
    user.moAccess ? moListCategories : [],
    user.roAccess ? roListCategories : [],
  );

  const {
    loading: listDataLoading,
    error: listDataError,
    data: listData,
  } = useQuery(LOAD_LIST_DATA, {
    fetchPolicy: "cache-first",
    variables: {
      listCategories,
      templateId,
    },
  });

  const {
    loading: diagnosisCategoryLoading,
    error: diagnosisCategoryError,
    data: diagnosisCategoryOptions,
  } = useQuery(GET_DIAGNOSIS_CATEGORIES, {
    fetchPolicy: "cache-first",
  });

  const {
    loading: siteLoading,
    error: siteError,
    data: siteOptions,
  } = useQuery(GET_SITES, {
    fetchPolicy: "cache-first",
    variables: {
      templateId,
    },
  });
  return (
    <>
      {loading &&
        listDataLoading &&
        diagnosisCategoryLoading &&
        siteLoading && <div>Loading...</div>}
      {data && listData && diagnosisCategoryOptions && siteOptions && (
        <CareplanCreatorForm
          template={data.template}
          reloadTemplate={reloadTemplate}
          listData={listData.dataReferenceList}
          diagnosisCategoryData={diagnosisCategoryOptions.diagnosisCategoryList}
          siteListData={siteOptions.siteList}
        />
      )}
      {error && listDataError && diagnosisCategoryError && siteError && (
        <div>Error loading...</div>
      )}
    </>
  );
};

export default CareplanCreatorFormApollo;
